import { posthogClient } from './posthog';
import { toDateString } from './utils';

export interface UserIdentity {
  userId: string;
  email: string;
}

export interface AuthEventProperties {
  method: string;
}

export interface PlanEventProperties {
  period: string;
  startAtLocal: string;
}

export interface ViewedPlanProperties extends PlanEventProperties {
  view: 'check-in' | 'plan';
}

export interface UpdatedPlanProperties {
  planPeriod: string;
  planStartAtLocal: string;
  field: string;
  previousValue: string;
  value: string;
}

export interface GoalAddedProperties extends PlanEventProperties {
  goalId: string;
  position: number;
  source: 'manual' | 'aiSuggestion';
}

export interface GoalUpdatedProperties extends PlanEventProperties {
  goalId: string;
  position: number;
}

export interface GoalCompletedProperties extends PlanEventProperties {
  goalId: string;
  position: number;
  doneAtLocal: string;
}

export interface GoalUncompletedProperties extends PlanEventProperties {
  goalId: string;
  position: number;
}

export interface GoalDeletedProperties extends PlanEventProperties {
  goalId: string;
  position: number;
}

export interface GoalReorderedProperties extends PlanEventProperties {
  goalId: string;
  fromPosition: number;
  toPosition: number;
}

export interface AiSentMessageProperties extends PlanEventProperties {
  conversationMessageCount: number;
}

export type AiSuggestionProperties = PlanEventProperties;

export const analyticsService = {
  identifyUser: (identity: UserIdentity): void => {
    const { userId, email } = identity;
    posthogClient.identify(userId, { email });
  },

  resetIdentity: (): void => {
    posthogClient.reset();
  },

  trackSignedUp: (properties: AuthEventProperties): void => {
    posthogClient.capture('Signed Up', { method: properties.method });
  },

  trackSignedIn: (properties: AuthEventProperties): void => {
    posthogClient.capture('Signed In', { method: properties.method });
  },

  trackSignedOut: (): void => {
    posthogClient.capture('Signed Out', {});
  },

  trackViewedPlan: (properties: ViewedPlanProperties): void => {
    posthogClient.capture('Viewed Plan', {
      period: properties.period,
      startAtLocal: toDateString(properties.startAtLocal),
      view: properties.view,
    });
  },

  trackUpdatedPlan: (properties: UpdatedPlanProperties): void => {
    posthogClient.capture('Updated Plan', {
      planPeriod: properties.planPeriod,
      planStartAtLocal: toDateString(properties.planStartAtLocal),
      field: properties.field,
      previousValue: properties.previousValue,
      value: properties.value,
    });
  },

  trackAddedGoal: (properties: GoalAddedProperties): void => {
    posthogClient.capture('Added Goal', {
      goalId: properties.goalId,
      planPeriod: properties.period,
      planStartAtLocal: toDateString(properties.startAtLocal),
      position: properties.position,
      source: properties.source,
    });
  },

  trackUpdatedGoal: (properties: GoalUpdatedProperties): void => {
    posthogClient.capture('Updated Goal', {
      goalId: properties.goalId,
      planPeriod: properties.period,
      planStartAtLocal: toDateString(properties.startAtLocal),
      position: properties.position,
    });
  },

  trackCompletedGoal: (properties: GoalCompletedProperties): void => {
    posthogClient.capture('Completed Goal', {
      goalId: properties.goalId,
      planPeriod: properties.period,
      planStartAtLocal: toDateString(properties.startAtLocal),
      position: properties.position,
      doneAtLocal: toDateString(properties.doneAtLocal),
    });
  },

  trackUncompletedGoal: (properties: GoalUncompletedProperties): void => {
    posthogClient.capture('Uncompleted Goal', {
      goalId: properties.goalId,
      planPeriod: properties.period,
      planStartAtLocal: toDateString(properties.startAtLocal),
      position: properties.position,
    });
  },

  trackDeletedGoal: (properties: GoalDeletedProperties): void => {
    posthogClient.capture('Deleted Goal', {
      goalId: properties.goalId,
      planPeriod: properties.period,
      planStartAtLocal: toDateString(properties.startAtLocal),
      position: properties.position,
    });
  },

  trackReorderedGoal: (properties: GoalReorderedProperties): void => {
    posthogClient.capture('Reordered Goal', {
      goalId: properties.goalId,
      planPeriod: properties.period,
      planStartAtLocal: toDateString(properties.startAtLocal),
      fromPosition: properties.fromPosition,
      toPosition: properties.toPosition,
    });
  },

  trackSentMessage: (properties: AiSentMessageProperties): void => {
    posthogClient.capture('Sent Message', {
      planPeriod: properties.period,
      planStartAtLocal: toDateString(properties.startAtLocal),
      conversationMessageCount: properties.conversationMessageCount,
    });
  },

  trackReceivedSuggestion: (properties: AiSuggestionProperties): void => {
    posthogClient.capture('Received Suggestion', {
      planPeriod: properties.period,
      planStartAtLocal: toDateString(properties.startAtLocal),
    });
  },

  trackAcceptedSuggestion: (properties: AiSuggestionProperties): void => {
    posthogClient.capture('Accepted Suggestion', {
      planPeriod: properties.period,
      planStartAtLocal: toDateString(properties.startAtLocal),
    });
  },

  trackSkippedSuggestion: (properties: AiSuggestionProperties): void => {
    posthogClient.capture('Skipped Suggestion', {
      planPeriod: properties.period,
      planStartAtLocal: toDateString(properties.startAtLocal),
    });
  },
};
